import PropTypes from 'prop-types';
import React from 'react';
import cx from 'classnames';
import InputMask from 'react-input-mask';
import PlainInput from '../PlainInput';
import Label from '../Label';
import styles from './Input.css';
import Loading from '../../Loading';

const Input = ({
    input,
    id,
    label,
    inputStyle,
    inputClassName,
    mask,
    maskChar,
    type,
    min,
    max,
    className,
    style,
    children,
    labelStyle,
    autoComplete,
    disabled,
    labelClassName,
    help,
    placeholder,
    describedById,
    required,
    meta: { touched, asyncValidating, error, warning },
}) => {
    let info;
    let infoClassName;
    let errorId;
    const inputId = id || input.name;
    const fieldError = touched && error ? error : null;
    if (fieldError) {
        infoClassName = styles.error;
        info = fieldError;
        errorId = `${input.name}-error`;
    } else if (touched && warning) {
        infoClassName = styles.warn;
        info = warning;
    } else if (help) {
        infoClassName = styles.help;
        info = typeof help === 'function' ?
            help(input.value) : help;
    } else if (asyncValidating) {
        infoClassName = styles.loading;
        info = (<Loading />);
    }
    const infoId = info ? `info-${id || input.name}` : undefined;
    const ariaInvalid = touched && !!error ? true : undefined;
    const describedByIds = [
        describedById,
        fieldError ? errorId : undefined,
    ].filter(Boolean);
    const ariaDescribedBy = describedByIds.length ?
        describedByIds.join(' ') : undefined;
    let inputEl;
    if (mask) {
        inputEl = (<InputMask
            mask={mask}
            maskChar={maskChar || '_'}
            {...input}
            onChange={(e) => {
                e.preventDefault();
                input.onChange(e.target.value);
            }}
            value={input.value}
            disabled={disabled}
            formatChars={{
                9: '[0-9]',
                a: '[A-Za-z]',
                '*': '[A-Za-z0-9]',
                m: '[0-9]',
                y: '[0-9]',
                d: '[0-9]',
            }}
            autoComplete={autoComplete}
            placeholder={placeholder}
        >
            {passProps => <PlainInput
                {...passProps}
                id={inputId}
                style={inputStyle}
                className={inputClassName}
                type={type}
                aria-describedby={ariaDescribedBy}
                aria-invalid={fieldError ? true : undefined}
                aria-required={required ? true : undefined}
            />}
        </InputMask>);
    } else {
        inputEl = (<PlainInput
            {...input}
            id={inputId}
            style={inputStyle}
            className={inputClassName}
            type={type}
            min={min}
            max={max}
            disabled={disabled}
            autoComplete={autoComplete}
            placeholder={placeholder}
            aria-describedby={ariaDescribedBy}
            aria-invalid={fieldError ? true : undefined}
            aria-required={required ? true : undefined}
        />);
    }
    return (<div
        className={cx(styles.container, className)}
        style={style}
    >
        <Label
            name={input.name}
            text={label}
            hidden={!label}
            htmlFor={input.name}
            className={labelClassName}
            style={labelStyle}
            id={`label-${inputId}`}
        />
        {inputEl}
        {children}
        {info ? <span
            id={errorId}
            role={fieldError ? 'alert' : undefined}
            className={cx(styles.info, infoClassName)}
        >
            {info}
        </span> : null}
    </div>);
};

Input.propTypes = {
    label: PropTypes.node,
    type: PropTypes.string,
    id: PropTypes.string,
    autoComplete: PropTypes.string,
    mask: PropTypes.string,
    maskChar: PropTypes.string,
    min: PropTypes.string,
    max: PropTypes.string,
    input: PropTypes.object,
    inputStyle: PropTypes.object,
    labelStyle: PropTypes.object,
    style: PropTypes.object,
    meta: PropTypes.object,
    className: PropTypes.string,
    help: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
    ]),
    children: PropTypes.node,
    labelClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    describedById: PropTypes.string,
    required: PropTypes.bool,
};

Input.defaultProps = {
    meta: {},
};

export default Input;



// WEBPACK FOOTER //
// ./src/client/Components/Forms/Input/Input.js